import { useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { currencyFormatter, textFormatter } from "../../../utils/formatter/formatter";
import { StableSelect } from "../../../components/SharedComponents/sharedComponents.index";
import { SYSTEM_COLORS } from "../../../components/system.colors";

const COLUMN_GROUPS = [
  {
    label: "Faturamento",
    headers: ["Plano", "Real", "LY", "% vs LY", "Dif $ vs LY"],
    columns: [
      {
        bold: false,
        render: row => currencyFormatter.format(row.fatPlano ?? 0),
      },
      {
        bold: true,
        render: row => currencyFormatter.format(row.fatReal ?? 0),
      },
      {
        bold: false,
        render: row => row.fatLY != null ? currencyFormatter.format(row.fatLY) : "—",
      },
      {
        bold: false,
        minWidth: 80,
        render: row =>
          row.fatVsLY != null ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                padding: "2px 7px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "600",
                color: row.isPositive ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED,
                backgroundColor: row.isPositive
                  ? "rgba(30,138,66,0.1)"
                  : "rgba(224,82,82,0.1)",
              }}
            >
              {row.isPositive ? "↑" : "↓"} {row.fatVsLY.toFixed(1)}%
            </span>
          ) : "—",
      },
      {
        bold: false,
        minWidth: 80,
        render: row =>
          row.fatDif != null ? (
            <span
              style={{
                fontSize: "12px",
                fontWeight: "500",
                color: row.isPositive ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED,
              }}
            >
              {currencyFormatter.format(row.fatDif)}
            </span>
          ) : "—",
      },
    ],
  },
  {
    label: "Venda Pçs",
    headers: ["Plano", "Real", "LY", "%"],
    columns: [
      {
        bold: false,
        render: row =>
          row.vendaPlano != null
            ? Number(row.vendaPlano).toLocaleString("pt-BR")
            : "—",
      },
      {
        bold: true,
        render: row =>
          row.vendaReal != null
            ? Number(row.vendaReal).toLocaleString("pt-BR")
            : "—",
      },
      {
        bold: false,
        render: row =>
          row.vendaLY != null
            ? Number(row.vendaLY).toLocaleString("pt-BR")
            : "—",
      },
      {
        bold: false,
        minWidth: 80,
        render: row =>
          row.vendaPct != null ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                padding: "2px 7px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "600",
                color: row.vendaPos ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED,
                backgroundColor: row.vendaPos
                  ? "rgba(30,138,66,0.1)"
                  : "rgba(224,82,82,0.1)",
              }}
            >
              {row.vendaPos ? "↑" : "↓"} {row.vendaPct.toFixed(1)}%
            </span>
          ) : "—",
      },
    ],
  },
  {
    label: "PM",
    headers: ["Plano", "Real", "LY"],
    columns: [
      {
        bold: false,
        render: row =>
          row.pmPlano != null ? `R$ ${row.pmPlano.toFixed(0)}` : "—",
      },
      {
        bold: true,
        render: row =>
          row.pmReal != null ? `R$ ${row.pmReal.toFixed(0)}` : "—",
      },
      {
        bold: false,
        render: row =>
          row.pmLY != null ? `R$ ${row.pmLY.toFixed(0)}` : "—",
      },
    ],
  },
  {
    label: "Markup",
    headers: ["Plano", "Real", "LY"],
    columns: [
      {
        bold: false,
        render: row =>
          row.mkPlano != null ? `${row.mkPlano.toFixed(2)}x` : "—",
      },
      {
        bold: true,
        render: row =>
          row.mkReal != null ? `${row.mkReal.toFixed(2)}x` : "—",
      },
      {
        bold: false,
        render: row =>
          row.mkLY != null ? `${row.mkLY.toFixed(2)}x` : "—",
      },
    ],
  },
  {
    label: "Estoque",
    headers: ["Real", "LY", "%"],
    columns: [
      {
        bold: true,
        render: row =>
          row.estoqueReal != null
            ? Number(row.estoqueReal).toLocaleString("pt-BR")
            : "—",
      },
      {
        bold: false,
        render: row =>
          row.estoqueLY != null
            ? Number(row.estoqueLY).toLocaleString("pt-BR")
            : "—",
      },
      {
        bold: false,
        minWidth: 80,
        render: row =>
          row.estoquePct != null ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                padding: "2px 7px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "600",
                color: row.estoquePos ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED,
                backgroundColor: row.estoquePos
                  ? "rgba(30,138,66,0.1)"
                  : "rgba(224,82,82,0.1)",
              }}
            >
              {row.estoquePos ? "↑" : "↓"} {row.estoquePct.toFixed(1)}%
            </span>
          ) : "—",
      },
    ],
  },
  {
    label: "Cobertura",
    headers: ["Real", "LY", "%"],
    columns: [
      {
        bold: true,
        render: row =>
          row.cobReal != null ? row.cobReal.toFixed(1) : "—",
      },
      {
        bold: false,
        render: row =>
          row.cobLY != null ? row.cobLY.toFixed(1) : "—",
      },
      {
        bold: false,
        minWidth: 80,
        render: row =>
          row.cobPct != null ? (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                padding: "2px 7px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "600",
                color: row.cobPos ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED,
                backgroundColor: row.cobPos
                  ? "rgba(30,138,66,0.1)"
                  : "rgba(224,82,82,0.1)",
              }}
            >
              {row.cobPos ? "↑" : "↓"} {row.cobPct.toFixed(1)}%
            </span>
          ) : "—",
      },
    ],
  },
];

const PERIOD_OPTIONS = [
  {
    label: textFormatter("ytd", { textTransform: 'uppercase' }),
    value: 0
  },
  {
    label: textFormatter("ano completo"),
    value: 1
  }
];

const ROW_HEIGHT = 52;
const SKELETON_ROWS = 8;
const TOTAL_COLUMNS = 1 + COLUMN_GROUPS.reduce((total, group) => total + group.headers.length, 0);

const KpiHeader = ({ ctx, uiState }) => {
  const { value, diff } = ctx || {};

  const {
    label,
    sub = "",
    diffLabel,
    diffPositive,
    alwaysArrow = false,
  } = uiState || {};

  const hasDiff = diff != null;
  const showArrow = hasDiff || alwaysArrow;
  const positive = alwaysArrow && !hasDiff ? true : diffPositive;
  const strokeColor = positive ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED;

  return (
    <section
      style={{
        backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
        border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
        borderRadius: "12px",
        boxShadow: `0 1px 3px ${SYSTEM_COLORS.BLACK.BLACK_4_PERCENT}`,
        padding: "18px 22px",
        flex: 1,
      }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6 }}>
        <div
          style={{
            fontSize: "11px", fontWeight: "600",
            color: SYSTEM_COLORS.GRAY.GREY, letterSpacing: "0.5px", textTransform: "uppercase" }}>
          {label}
        </div>

        {showArrow &&
          (positive ? (
            <svg
              width={16} height={16} viewBox="0 0 24 24" fill="none"
              stroke={strokeColor}
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3,17 9,11 13,15 21,7" />
              <polyline points="14,7 21,7 21,14" />
            </svg>
          ) : (
            <svg
              width={16} height={16} viewBox="0 0 24 24" fill="none"
              stroke={strokeColor}
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3,7 9,13 13,9 21,17" />
              <polyline points="14,17 21,17 21,10" />
            </svg>
          ))}
      </div>

      <div style={{ fontSize: "24px", fontWeight: "700", color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
        {value}
      </div>

      {hasDiff ? (
        <div style={{ fontSize: "12px", color: diffPositive ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.RED.BRICK_RED, marginTop: 4 }}>
          {Math.abs(diff).toFixed(1)}%{" "}
          {diffPositive ? "acima" : "abaixo"} {diffLabel}
        </div>
      ) : (
        sub && (
          <div style={{ fontSize: "12px", color: SYSTEM_COLORS.GRAY.GREY, marginTop: 4 }}>
            {sub}
          </div>
        )
      )}
    </section>
  );
};

const PerformanceCategoriaTable = ({ rows, isLoading }) => {
  const scrollRef = useRef(null);

  const virtualizer = useVirtualizer({
    count: isLoading ? 0 : rows?.length ?? 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  if (!isLoading && !rows?.length) {
    return (
      <div style={{ padding: 20, color: "#999", fontSize: "13px" }}>
        Dados não disponíveis.
      </div>
    );
  }

  const virtualRows = virtualizer.getVirtualItems();
  const paddingTop = virtualRows.length ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length
    ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0;

  return (
    <section ref={scrollRef} style={{ overflow: "auto", inlineSize: "100%", maxHeight: "70vh" }}>
      <style>{`@keyframes performance-skeleton-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
      <table style={{ inlineSize: "max-content", minInlineSize: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ height: 30 }}>
            <th
              rowSpan={2}
              style={{
                padding: "7px 12px",
                backgroundClip: "padding-box",
                fontSize: "11px",
                color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                fontWeight: "700",
                borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.GAINSBORO}`,
                textAlign: "left",
                whiteSpace: "nowrap",
                borderRight: `2px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                position: "sticky",
                left: 0,
                top: 0,
                backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
                zIndex: 4,
              }}
            >
              Categoria
            </th>

            {COLUMN_GROUPS.map((group, groupIndex) => (
              <th
                key={group.label}
                colSpan={group.headers.length}
                style={{
                  padding: "7px 12px",
                  fontSize: "11px",
                  color: "#777",
                  fontWeight: "700",
                  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.GAINSBORO}`,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderRight:
                    groupIndex === COLUMN_GROUPS.length - 1
                      ? "none"
                      : "2px solid #ddd",
                  position: "sticky",
                  top: 0,
                  backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
                  backgroundClip: "padding-box",
                  zIndex: 2,
                }}
              >
                {group.label}
              </th>
            ))}
          </tr>

          <tr style={{ height: 30 }}>
            {COLUMN_GROUPS.flatMap((group, groupIndex) =>
              group.headers.map((header, columnIndex) => (
                <th
                  key={`${group.label}-${header}`}
                  style={{
                    padding: "7px 12px",
                    fontSize: "11px",
                    color: "#777",
                    fontWeight: "600",
                    borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.GAINSBORO}`,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    borderRight:
                      columnIndex === group.headers.length - 1
                        ? groupIndex === COLUMN_GROUPS.length - 1
                          ? "none"
                          : "2px solid #ddd"
                        : "1px solid #f0f0f0",
                    position: "sticky",
                    top: 28,
                    backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
                    backgroundClip: "padding-box",
                    zIndex: 3,
                  }}
                >
                  {header}
                </th>
              ))
            )}
          </tr>
        </thead>

        <tbody>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
              <tr key={`skeleton-${rowIndex}`} style={{ height: ROW_HEIGHT }}>
                {Array.from({ length: TOTAL_COLUMNS }, (_, columnIndex) => (
                  <td
                    key={columnIndex}
                    style={{
                      padding: "9px 12px",
                      backgroundColor: rowIndex % 2 === 0 ? SYSTEM_COLORS.WHITE.WHITE : "#fafafa",
                      borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.GAINSBORO}`,
                    }}
                  >
                    <div
                      style={{
                        height: 12,
                        inlineSize: columnIndex === 0 ? 140 : 64,
                        marginInlineStart: columnIndex === 0 ? 0 : "auto",
                        borderRadius: 4,
                        backgroundColor: SYSTEM_COLORS.GRAY.GAINSBORO,
                        animation: "performance-skeleton-pulse 1.2s ease-in-out infinite",
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <>
              {paddingTop > 0 && (
                <tr><td colSpan={TOTAL_COLUMNS} style={{ height: paddingTop, padding: 0, border: "none" }} /></tr>
              )}

              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];
                const rowIndex = virtualRow.index;

                return (
                  <tr
                    key={`${row.categoria}-${row.rootLevel}`}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement}
                  >
                    <td
                      style={{
                        padding: "9px 12px",
                        textAlign: "left",
                        fontSize: "12.5px",
                        color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
                        fontWeight: "400",
                        whiteSpace: "nowrap",
                        backgroundColor: rowIndex % 2 === 0 ? SYSTEM_COLORS.WHITE.WHITE : "#fafafa",
                        borderRight: `2px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
                        borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.GAINSBORO}`,
                        borderTop: `1px solid ${SYSTEM_COLORS.GRAY.GAINSBORO}`,
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                      }}>
                      <div>{row.categoria}</div>
                      <div style={{ fontSize: "10.5px", color: "#999", fontWeight: 400 }}>
                        {row.rootLevel}
                      </div>
                    </td>

                    {COLUMN_GROUPS.flatMap((group, groupIndex) =>
                      group.columns.map((column, columnIndex) => (
                        <td
                          key={`${group.label}-${columnIndex}`}
                          style={{
                            padding: "9px 12px",
                            textAlign: "right",
                            fontSize: "12.5px",
                            color: "#333",
                            fontWeight: column.bold ? "700" : "400",
                            whiteSpace: "nowrap",
                            backgroundColor: rowIndex % 2 === 0 ? SYSTEM_COLORS.WHITE.WHITE : "#fafafa",
                            borderRight:
                              columnIndex === group.columns.length - 1
                                ? groupIndex === COLUMN_GROUPS.length - 1
                                  ? "none"
                                  : "2px solid #ddd"
                                : "1px solid #f0f0f0",
                            borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.GAINSBORO}`,
                            borderTop: `1px solid ${SYSTEM_COLORS.GRAY.GAINSBORO}`,
                            ...(column.minWidth && {
                              minWidth: column.minWidth,
                            }),
                          }}
                        >
                          {column.render(row)}
                        </td>
                      ))
                    )}
                  </tr>
                );
              })}

              {paddingBottom > 0 && (
                <tr><td colSpan={TOTAL_COLUMNS} style={{ height: paddingBottom, padding: 0, border: "none" }} /></tr>
              )}
            </>
          )}
        </tbody>
      </table>
    </section>
  );
};

export function PerformanceSection({ ctx, actions, uiState }) {
  const { performanceData, otbCycles, selectedPerformanceCycleId } = ctx || {};
  const { setPerformanceCycleId } = actions || {};
  const { isLoading = false } = uiState || {};

  const [period, setPeriod] = useState(PERIOD_OPTIONS[0].value);
  const currentMonth = new Date().getMonth() + 1;

  const availableYears = useMemo(() => {
    return [...new Set(
      (performanceData || [])
        .map(r => Number(r.year))
        .filter(y => !isNaN(y))
    )].sort();
  }, [performanceData]);

  const selectedYear = useMemo(() => {
    if (!availableYears.length) return null;
    return Math.max(...availableYears);
  }, [availableYears]);

  const groupKeyOf = (row) => `${row.filterLevelName || row.categoria}||${row.rootLevel}`;

  const aggregateByCategoria = (rows) => {
    const grouped = {};

    rows.forEach(row => {
      const key = groupKeyOf(row);

      if (!grouped[key]) {
        grouped[key] = {
          productHierarchyNodeId: row.productHierarchyNodeId,
          rootLevel: row.rootLevel,
          categoria: row.filterLevelName || row.categoria,
          filterLevelName: row.filterLevelName,
          months: [],
        };
      }

      grouped[key].months.push(row);
    });

    return Object.values(grouped).map(group => {
      const months = group.months.slice().sort((a, b) => a.month - b.month);
      const lastMonth = months[months.length - 1];

      const sum = (field) =>
        months.reduce((acc, m) => acc + (Number(m[field]) || 0), 0);

      const weightedAvg = (valueField, weightField) => {
        let totalWeight = 0;
        let totalValue = 0;

        months.forEach(m => {
          const weight = Number(m[weightField]) || 0;
          const value = m[valueField] != null ? Number(m[valueField]) : null;
          if (value != null && weight > 0) {
            totalValue += value * weight;
            totalWeight += weight;
          }
        });

        return totalWeight > 0 ? totalValue / totalWeight : null;
      };

      return {
        categoria: group.categoria,
        rootLevel: group.rootLevel,
        filterLevelName: group.filterLevelName,

        fatPlano: sum('fatPlano'),
        fatReal: sum('fatReal'),
        vendaPlano: sum('vendaPlano'),
        vendaReal: sum('vendaReal'),

        lucroPlano: sum('lucroPlano'),
        lucroReal: sum('lucroReal'),

        pmPlano: weightedAvg('pmPlano', 'vendaPlano'),
        pmReal: weightedAvg('pmReal', 'vendaReal'),
        mkPlano: weightedAvg('mkPlano', 'vendaPlano'),
        mkReal: weightedAvg('mkReal', 'vendaReal'),

        estoqueReal:
          lastMonth?.estoqueReal != null ? Number(lastMonth.estoqueReal) : null,
        cobReal: lastMonth?.cobReal != null ? Number(lastMonth.cobReal) : null,
      };
    });
  };

  const yearData = useMemo(() => {
    if (!selectedYear) return [];

    const rawRows = (performanceData || []).filter(r => {
      if (r.year !== selectedYear) return false;
      if (period === 0) return r.month != null && r.month <= currentMonth;
      return true;
    });

    return aggregateByCategoria(rawRows);
  }, [performanceData, selectedYear, period, currentMonth]);

  const yearLYData = useMemo(() => {
    if (!selectedYear) return [];

    const rawRows = (performanceData || []).filter(r => {
      if (r.year !== selectedYear - 1) return false;
      if (period === 0) return r.month != null && r.month <= currentMonth;
      return true;
    });

    return aggregateByCategoria(rawRows);
  }, [performanceData, selectedYear, period, currentMonth]);

  const fatPlanoTotal = yearData.reduce((s, r) => s + (r.fatPlano ?? 0), 0);
  const fatRealTotal = yearData.reduce((s, r) => s + (r.fatReal ?? 0), 0);
  const fatLYTotal = yearLYData.reduce((s, r) => s + (r.fatReal ?? 0), 0);

  const fatRealDiff = fatPlanoTotal
    ? (fatRealTotal / fatPlanoTotal - 1) * 100
    : null;
  const fatLYDiff = fatLYTotal ? (fatRealTotal / fatLYTotal - 1) * 100 : null;

  const cycleOptions = useMemo(() => {
    return (otbCycles || []).map(cycle => ({
      label: cycle.otbName,
      value: cycle.cycleId,
    }));
  }, [otbCycles]);

  const filteredCategoryData = useMemo(() => {
    if (!selectedYear) return [];

    const lyMap = {};
    yearLYData.forEach(r => {
      lyMap[groupKeyOf(r)] = r;
    });

    return yearData.map(r => {
      const lyRow = lyMap[groupKeyOf(r)] || {};

      const fatLY = lyRow.fatReal ?? null;
      const fatReal = r.fatReal ?? null;
      const fatVsLY = fatLY && fatReal ? (fatReal / fatLY - 1) * 100 : null;
      const fatDif = fatReal != null && fatLY != null ? fatReal - fatLY : null;

      const vendaLY = lyRow.vendaReal ?? null;
      const vendaReal = r.vendaReal ?? null;
      const vendaPct =
        vendaLY && vendaReal ? (vendaReal / vendaLY - 1) * 100 : null;

      const estoqueLY = lyRow.estoqueReal ?? null;
      const estoqueReal = r.estoqueReal ?? null;
      const estoquePct =
        estoqueLY && estoqueReal ? (estoqueReal / estoqueLY - 1) * 100 : null;

      const cobLY = lyRow.cobReal ?? null;
      const cobReal = r.cobReal ?? null;
      const cobPct = cobLY && cobReal ? (cobReal / cobLY - 1) * 100 : null;

      return {
        ...r,
        fatLY,
        fatVsLY,
        fatDif,
        isPositive: fatVsLY != null ? fatVsLY >= 0 : false,

        vendaLY,
        vendaPct,
        vendaPos: vendaPct != null ? vendaPct >= 0 : false,

        pmLY: lyRow.pmReal ?? null,
        mkLY: lyRow.mkReal ?? null,

        estoqueLY,
        estoquePct,
        estoquePos: estoquePct != null ? estoquePct >= 0 : false,

        cobLY,
        cobPct,
        cobPos: cobPct != null ? cobPct >= 0 : false,
      };
    });
  }, [yearData, yearLYData, selectedYear]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          inlineSize: "100%",
        }}
      >
        <hgroup>
          <h1
            style={{
              fontSize: "22px",
              fontWeight: "700",
              color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
              margin: 0,
            }}
          >
            Performance
          </h1>
          <p style={{ fontSize: "13px", color: "#999", margin: "3px 0 0" }}>
            Comparativo Plano vs Realizado vs Mesmo período do ano anterior
          </p>
        </hgroup>

        <section style={{ display: "flex", gap: "1rem" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              inlineSize: "fit-content",
            }}
          >
            <p style={{ fontSize: "0.8rem" }}>{"Estação"}</p>
            <StableSelect
              label=""
              mode="rovitex"
              value={selectedPerformanceCycleId}
              onChange={setPerformanceCycleId}
              options={cycleOptions}
              showCheckMark={true}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              inlineSize: "fit-content",
            }}
          >
            <p style={{ fontSize: "0.8rem" }}>{"Período de análise"}</p>
            <StableSelect
              label=""
              mode="rovitex"
              value={period}
              onChange={setPeriod}
              options={PERIOD_OPTIONS}
              showCheckMark={true}
            />
          </div>
        </section>
      </div>

      <div
        style={{
          display: "flex",
          inlineSize: "fit-content",
          justifyContent: "end",
          gap: 14,
          opacity: isLoading ? 0.5 : 1,
          transition: "opacity 0.2s",
        }}
      >
        <KpiHeader
          ctx={{ value: isLoading ? "—" : currencyFormatter.format(fatPlanoTotal) }}
          uiState={{
            label: "Faturamento Plano",
            sub: selectedYear ? `Ano de ${selectedYear}` : "",
            alwaysArrow: !isLoading,
          }}
        />

        <KpiHeader
          ctx={{
            value: isLoading ? "—" : currencyFormatter.format(fatRealTotal),
            diff: isLoading ? null : fatRealDiff
          }}
          uiState={{
            label: "Faturamento Real",
            diffLabel: "do plano",
            diffPositive: fatRealDiff != null ? fatRealDiff >= 0 : true,
          }}
        />

        <KpiHeader
          ctx={{
            value: isLoading ? "—" : currencyFormatter.format(fatLYTotal),
            diff: isLoading ? null : fatLYDiff
          }}
          uiState={{
            label: "Faturamento LY",
            diffLabel: selectedYear ? `vs Real ${selectedYear - 1}` : "vs LY",
            diffPositive: fatLYDiff != null ? fatLYDiff >= 0 : true,
          }}
        />
      </div>

      <hgroup
        style={{
          backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
          border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          padding: "14px 20px",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "700",
            color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
            margin: 0,
          }}
        >
          Performance por Categoria
        </h3>
      </hgroup>

      <section
        style={{
          backgroundColor: SYSTEM_COLORS.WHITE.WHITE,
          border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          marginBlockEnd: "1rem",
        }}
      >
        <PerformanceCategoriaTable rows={filteredCategoryData || []} isLoading={isLoading} />
      </section>
    </div>
  );
}